import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    TextField,
    Button,
    Grid,
    Typography,
    Card,
    CardContent,
    CardActions,
    CircularProgress,
    Box
} from '@mui/material';
import dayjs from 'dayjs';
import { DataGrid } from '@mui/x-data-grid';
import { t } from 'i18next';
import { useUsers } from '../../../hooks/useUsers';
import {
    patchGroupData,
    fetchGroupByUUID,
    updateGroupRoles,
    deleteGroup
} from '../../../services/groups';

const EditGroup = () => {
    const { groupId } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [group, setGroup] = useState(null);
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [selectedRoleUUIDs, setSelectedRoleUUIDs] = useState([]);
    const [selectedDoctorsRoleUUIDs, setSelectedDoctorsRoleUUIDs] = useState([]);
    const [search, setSearch] = useState('');
    const [initialPatientUUIDs, setInitialPatientUUIDs] = useState([]);
    const [initialDoctorsUUIDs, setInitialDoctorsUUIDs] = useState([]);

    const {
        data: patientRows = [],
        isLoading,
        paginationModel,
        setPaginationModel,
        count,
        rowsPerPageOptions
    } = useUsers(['PATIENT']);

    const {
        data: doctorsRows = [],
        isLoadingDoctor,
        paginationModelDoctor,
        setPaginationModelDoctor,
        countDoctor,
        rowsPerPageOptionsDoctor
    } = useUsers(['DOCTOR']);

    const filteredRows = patientRows.filter((p) =>
        p.name.toLowerCase().includes(search.toLowerCase())
    );

    const filteredRowsMedico = doctorsRows.filter((p) =>
        p.name.toLowerCase().includes(search.toLowerCase())
    );

    useEffect(() => {
        const fetchGroup = async () => {
            try {
                const data = await fetchGroupByUUID(groupId);
                setGroup(data);
                setName(data.name);
                setDescription(data.description);
                setStartDate(dayjs(data.start_date).format('YYYY-MM-DD'));
                setEndDate(dayjs(data.end_date).format('YYYY-MM-DD'));

                const patientUUIDs = data.patients?.map((p) => p.uuid) || [];
                setSelectedRoleUUIDs(patientUUIDs);
                setInitialPatientUUIDs(patientUUIDs);

                const doctorsUUIDs = data.staff?.map((p) => p.uuid) || [];
                setSelectedDoctorsRoleUUIDs(doctorsUUIDs);
                setInitialDoctorsUUIDs(doctorsUUIDs);
            } catch (error) {
                console.error('Erro ao procurar grupo:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchGroup();
    }, [groupId]);

    const handleUpdateGroup = async () => {
        const updatedGroupData = {
            uuid: groupId,
            name,
            description,
            start_date: startDate,
            end_date: endDate
        };

        const to_add = [...selectedRoleUUIDs, ...selectedDoctorsRoleUUIDs].filter(
            (uuid) => ![...initialPatientUUIDs, ...initialDoctorsUUIDs].includes(uuid)
        );
        const to_remove = [...initialPatientUUIDs, ...initialDoctorsUUIDs].filter(
            (uuid) => ![...selectedRoleUUIDs, ...selectedDoctorsRoleUUIDs].includes(uuid)
        );

        try {
            await patchGroupData(updatedGroupData);

            if (to_add.length > 0 || to_remove.length > 0) {
                await updateGroupRoles({
                    uuid: groupId,
                    to_add,
                    to_remove
                });
            }

            console.log('Grupo atualizado com sucesso');
            navigate('/grupos');
        } catch (error) {
            console.error('Erro ao atualizar grupo:', error);
        }
    };

    const handleDeleteGroup = async () => {
        if (!window.confirm(t('groups.delete_confirm'))) {
            return;
        }

        try {
            await deleteGroup(groupId);
            console.log('Grupo eliminado com sucesso');
            navigate('/grupos');
        } catch (error) {
            console.error('Erro ao eliminar grupo:', error);
        }
    };

    if (loading) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="300px"
            >
                <CircularProgress sx={{ color: '#1976d2' }} />
            </Box>
        );
    }

    if (!group) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="300px"
                textAlign="center"
            >
                <Typography variant="h5" color="text.secondary">
                    {t('groups.not_found')}
                </Typography>
            </Box>
        );
    }

    return (
        <Card sx={{ m: 2 }}>
            <CardContent>
                <Typography variant="h4" gutterBottom>
                    {t('groups.edit_group')}
                </Typography>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label={t('groups.name_label')}
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label={t('groups.description_label')}
                            multiline
                            rows={3}
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            type="date"
                            label={t('groups.start_date_label')}
                            InputLabelProps={{ shrink: true }}
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            type="date"
                            label={t('groups.end_date_label')}
                            InputLabelProps={{ shrink: true }}
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                        />
                    </Grid>
                </Grid>
                <Typography variant="h6" gutterBottom>
                    {t('groups.select_patients')}
                </Typography>

                <TextField
                    label={t('groups.search_patient_label')}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    fullWidth
                    sx={{ mb: 2 }}
                />

                <DataGrid
                    sx={{ mb: 3 }}
                    autoHeight
                    checkboxSelection
                    disableRowSelectionOnClick
                    disableColumnMenu
                    loading={isLoading}
                    rows={filteredRows}
                    getRowId={(row) => row.role_uuid}
                    columns={[
                        {
                            field: 'name',
                            headerName: t('groups.table_name_header'),
                            flex: 1
                        }
                    ]}
                    rowSelectionModel={selectedRoleUUIDs}
                    onRowSelectionModelChange={(selectionModel) => {
                        const currentPageUUIDs = filteredRows.map((row) => row.role_uuid);

                        const updatedSelection = selectedRoleUUIDs.filter(
                            (id) => !currentPageUUIDs.includes(id)
                        );

                        const newSelection = [
                            ...new Set([...updatedSelection, ...selectionModel])
                        ];

                        setSelectedRoleUUIDs(newSelection);
                    }}
                    paginationMode="server"
                    rowCount={count}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    pageSizeOptions={rowsPerPageOptions}
                />

                <Typography variant="h6" gutterBottom>
                    {t('groups.manage_doctors')}
                </Typography>

                <DataGrid
                    autoHeight
                    checkboxSelection
                    disableRowSelectionOnClick
                    disableColumnMenu
                    loading={isLoadingDoctor}
                    rows={filteredRowsMedico}
                    getRowId={(row) => row.role_uuid}
                    columns={[
                        {
                            field: 'name',
                            headerName: t('groups.table_name_header'),
                            flex: 1
                        }
                    ]}
                    rowSelectionModel={selectedDoctorsRoleUUIDs}
                    onRowSelectionModelChange={(selection) =>
                        setSelectedDoctorsRoleUUIDs(selection)
                    }
                    paginationMode="server"
                    rowCount={countDoctor}
                    paginationModel={paginationModelDoctor}
                    onPaginationModelChange={setPaginationModelDoctor}
                    pageSizeOptions={rowsPerPageOptionsDoctor}
                />
            </CardContent>

            <CardActions sx={{ justifyContent: 'space-between', p: 2 }}>
                <Box display="flex" gap={2}>
                    <Button variant="outlined" onClick={() => navigate('/grupos')}>
                        {t('groups.cancel')}
                    </Button>
                    <Button variant="outlined" color="error" onClick={handleDeleteGroup}>
                        {t('groups.delete_group')}
                    </Button>
                </Box>
                <Button variant="contained" color="primary" onClick={handleUpdateGroup}>
                    {t('groups.save_changes')}
                </Button>
            </CardActions>
        </Card>
    );
};

export default EditGroup;
